import type { Expand } from "../types/Expand"

/**
 * Creates a new object with only the specified keys.
 * Returns a shallow copy containing only the picked keys.
 *
 * @param obj - Source object
 * @param keys - Array of keys to include
 * @returns New object with only the specified keys
 *
 * @example
 * ```ts
 * import { KeyTake } from "@pdfking/tfunc"
 *
 * const user = { name: "John", age: 30, password: "secret" }
 * const safe = KeyTake(user, ["name", "age"])
 * // { name: "John", age: 30 }
 *
 * const nameOnly = KeyTake(user, ["name"] as const)
 * // { name: "John" }
 * ```
 */
export const KeyTake = <T extends object, K extends keyof T>(
  obj: T,
  keys: readonly K[]
): Expand<Pick<T, K>> => {
  const result = {} as Pick<T, K>
  for (const key of keys) {
    if (key in obj) {
      result[key] = obj[key]
    }
  }
  return result as unknown as Expand<Pick<T, K>>
}
